import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileSignature, Download, Plus, Trash2, AlertTriangle } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import useByggherreAccess from "@/hooks/useByggherreAccess";
import ReadOnlyNotice from "@/components/BasProcess/ReadOnlyNotice";
import DelegationExpiryBanner from "@/components/BasProcess/DelegationExpiryBanner";
import {
  DELEGATION_LETTER_STATUS_LABELS,
  DELEGATION_LETTER_TEMPLATE,
} from "@/lib/basProcess/byggherreDocs";
import { generateDelegationLetterWordDocument } from "@/lib/documentGenerator/byggherreWordGenerators";

interface Letter {
  id: string;
  from_name: string | null;
  from_role: string | null;
  to_name: string | null;
  to_role: string | null;
  scope: string | null;
  authority: string | null;
  competence_note: string | null;
  may_delegate_further: boolean;
  deputy_name: string | null;
  valid_from: string | null;
  valid_to: string | null;
  signed_at: string | null;
  status: string;
  note: string | null;
  sort_order: number;
}

export const DelegationLetter: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [letters, setLetters] = useState<Letter[]>([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const { canManage } = useByggherreAccess(projectId);

  const load = async () => {
    const [{ data, error }, { data: proj }] = await Promise.all([
      supabase
        .from("delegation_letters")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true }),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    if (error) toast.error("Kunde inte läsa delegeringsbreven");
    setProjectName(proj?.name ?? "");
    setLetters((data as unknown as Letter[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const add = async () => {
    const { error } = await supabase.from("delegation_letters").insert({
      project_id: projectId,
      scope: DELEGATION_LETTER_TEMPLATE.scope,
      authority: DELEGATION_LETTER_TEMPLATE.authority,
      competence_note: DELEGATION_LETTER_TEMPLATE.competence,
      sort_order: letters.length + 1,
    });
    if (error) return toast.error("Kunde inte skapa delegeringsbrevet");
    toast.success("Delegeringsbrev skapat med förifylld standardtext");
    load();
  };

  const update = async (id: string, patch: Partial<Letter>) => {
    setLetters((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    const { error } = await supabase.from("delegation_letters").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("delegation_letters").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort brevet");
    setLetters((prev) => prev.filter((l) => l.id !== id));
  };

  const exportWord = async (letter: Letter) => {
    try {
      await generateDelegationLetterWordDocument(letter, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const signed = letters.filter((l) => l.status === "undertecknat").length;

  if (loading)
    return <div className="py-12 text-center text-muted-foreground">Laddar delegeringsbrev...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="52"
        exportHistory={{ documentType: "delegeringsbrev", projectId }}
        audit={{ documentType: "delegeringsbrev", projectId, title: "Ändringslogg – delegeringsbrev" }}
        title="Delegeringsbrev"
        description="Personligt brev där arbetsmiljöuppgifter och befogenheter fördelas till en namngiven mottagare. Utan undertecknat brev saknar byggherrens representant dokumenterat mandat."
        legalRef="AML 3 kap. 6 § · AFS 2023:3 kap. 2 · AFS 2023:11"
        icon={FileSignature}
        meta={
          letters.length ? (
            <Badge variant={signed === letters.length ? "outline" : "destructive"}>
              {signed}/{letters.length} undertecknade
            </Badge>
          ) : null
        }
        actions={
          <Button size="sm" onClick={add} disabled={!canManage}>
            <Plus className="mr-1 h-4 w-4" /> Nytt brev
          </Button>
        }
      />

      <DelegationExpiryBanner projectId={projectId} canManage={canManage} />
      {!canManage && <ReadOnlyNotice />}

      {!letters.length ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <FileSignature className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Inga delegeringsbrev registrerade. Skapa ett brev per mottagare i kedjan, från VD ned till
              byggherrens representant.
            </p>
            <Button onClick={add} disabled={!canManage}>Skapa delegeringsbrev</Button>
          </CardContent>
        </Card>
      ) : (
        letters.map((l) => (
          <Card key={l.id}>
            <CardContent className="space-y-3 pt-6">
             <fieldset disabled={!canManage} className="space-y-3 disabled:opacity-90">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-semibold">
                    {l.from_name || "Fördelare"} → {l.to_name || "Mottagare"}
                  </h2>
                  <Badge variant={l.status === "undertecknat" ? "outline" : "secondary"}>
                    {DELEGATION_LETTER_STATUS_LABELS[l.status] || l.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={l.status} onValueChange={(v) => update(l.id, { status: v })}>
                    <SelectTrigger className="w-52">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DELEGATION_LETTER_STATUS_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => exportWord(l)}>
                    <Download className="mr-1 h-4 w-4" /> Word
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Ta bort brevet"
                    onClick={() => remove(l.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {l.status === "undertecknat" && !l.signed_at && (
                <p className="flex items-center gap-2 text-xs text-destructive">
                  <AlertTriangle className="h-4 w-4" /> Ange datum för undertecknande.
                </p>
              )}

              <div className="grid gap-2 sm:grid-cols-2">
                <Input
                  key={`fn-${l.id}`}
                  placeholder="Fördelas av – namn"
                  defaultValue={l.from_name ?? ""}
                  onBlur={(e) => update(l.id, { from_name: e.target.value })}
                />
                <Input
                  key={`fr-${l.id}`}
                  placeholder="Fördelas av – roll (t.ex. VD)"
                  defaultValue={l.from_role ?? ""}
                  onBlur={(e) => update(l.id, { from_role: e.target.value })}
                />
                <Input
                  key={`tn-${l.id}`}
                  placeholder="Mottagare – namn"
                  defaultValue={l.to_name ?? ""}
                  onBlur={(e) => update(l.id, { to_name: e.target.value })}
                />
                <Input
                  key={`tr-${l.id}`}
                  placeholder="Mottagare – roll (t.ex. byggherrens representant)"
                  defaultValue={l.to_role ?? ""}
                  onBlur={(e) => update(l.id, { to_role: e.target.value })}
                />
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Gäller från</label>
                  <Input
                    key={`vf-${l.id}`}
                    type="date"
                    defaultValue={l.valid_from ?? ""}
                    onBlur={(e) => update(l.id, { valid_from: e.target.value || null })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Gäller till (valfritt)</label>
                  <Input
                    key={`vt-${l.id}`}
                    type="date"
                    defaultValue={l.valid_to ?? ""}
                    onBlur={(e) => update(l.id, { valid_to: e.target.value || null })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Undertecknat</label>
                  <Input
                    key={`sa-${l.id}`}
                    type="date"
                    defaultValue={l.signed_at ?? ""}
                    onBlur={(e) => update(l.id, { signed_at: e.target.value || null })}
                  />
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <Input
                  key={`dp-${l.id}`}
                  placeholder="Ställföreträdare vid frånvaro"
                  defaultValue={l.deputy_name ?? ""}
                  onBlur={(e) => update(l.id, { deputy_name: e.target.value })}
                />
                <div className="flex items-center gap-3 rounded-lg border px-3 py-2">
                  <Switch
                    checked={l.may_delegate_further}
                    onCheckedChange={(v) => update(l.id, { may_delegate_further: v })}
                  />
                  <span className="text-sm">
                    Får fördela uppgiften vidare
                    <span className="block text-xs text-muted-foreground">
                      Annars får mottagaren endast låta annan handlägga.
                    </span>
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Omfattning</label>
                <Textarea
                  key={`sc-${l.id}`}
                  rows={3}
                  defaultValue={l.scope ?? ""}
                  onBlur={(e) => update(l.id, { scope: e.target.value })}
                />
                <label className="text-xs text-muted-foreground">Befogenheter</label>
                <Textarea
                  key={`au-${l.id}`}
                  rows={3}
                  defaultValue={l.authority ?? ""}
                  onBlur={(e) => update(l.id, { authority: e.target.value })}
                />
                <label className="text-xs text-muted-foreground">Utbildning och kompetens</label>
                <Textarea
                  key={`co-${l.id}`}
                  rows={2}
                  defaultValue={l.competence_note ?? ""}
                  onBlur={(e) => update(l.id, { competence_note: e.target.value })}
                />
                <label className="text-xs text-muted-foreground">Kommentar</label>
                <Input
                  key={`nt-${l.id}`}
                  defaultValue={l.note ?? ""}
                  onBlur={(e) => update(l.id, { note: e.target.value })}
                />
              </div>
             </fieldset>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default DelegationLetter;
